/* eslint-disable tailwindcss/no-custom-classname */
import { memo, useState, useCallback } from "react";

import {
  IndexReservation,
  OrderSettlementMethod,
  OrderStatus,
  Pagination as PaginationCondition,
  ServiceType,
  SortDirection,
  SubscriptionContractStatus,
} from "@/apis/clients";
import { useUserAccount } from "@/states/auth/selectoers";
import { Button } from "@/components/atoms/buttons/Button";
import OutlineButton from "@/components/atoms/buttons/OutlineButton";
import { Checkbox } from "@/components/atoms/inputForms/Checkbox";
import { Select } from "@/components/atoms/inputForms/Select";
import Pagination from "@/components/molecules/Pagination";
import { ReservationOrderStatusBadge } from "@/components/organisms/reservations/index/ReservationOrderStatusBadge";
import { SubscriptionOrderStatusBadge } from "@/components/atoms/budge/SubscriptionOrderStatusBadge";
import { DATE_FORMAT_2, HOURS_MINUTES_FORMAT } from "@/utils/const";
import { format } from "@/utils/date";
import ErrorLimitByPlanModal from "../../modals/ErrorLimitByPlanModal";
import Dropdown from "@/components/molecules/Dropdown";
import {
  orderSettlementMethodToString,
  updateStatusString,
} from "@/libs/enums/enumStrings";
import reservationApi from "@/apis/reservation/reservationApi";
import { formatNumber } from "@/utils/common";

interface IProps {
  reservations: IndexReservation[];
  isAll: boolean;
  checkedIds: number[];
  unCheckedIds: number[];
  handleClickAll: () => void;
  handleClickCheckbox: (id: number) => void;
  handleEditReservation: (id: number) => void;
  handleClickShow: (id: number) => void;
  handleClickSort: (direction: SortDirection) => void;
  sortState: SortDirection | undefined;
  pagination: PaginationCondition | undefined;
  handlePagination: (page: number, per: number | undefined) => Promise<void>;
  handleClickCsv: () => void;
  handleClickPaymentConfirm: (id: number) => void;
  handleRefreshReservations: () => void;
}

const sortOptions: { text: string; value: SortDirection }[] = [
  { text: "予約申込日が新しい順", value: SortDirection.Desc },
  { text: "予約申込日が古い順", value: SortDirection.Asc },
];

const ReservationTable = ({
  reservations,
  isAll,
  checkedIds,
  unCheckedIds,
  handleClickAll,
  handleClickCheckbox,
  handleClickSort,
  sortState,
  pagination,
  handlePagination,
  handleClickCsv,
  handleEditReservation,
  handleClickShow,
  handleClickPaymentConfirm,
  handleRefreshReservations,
}: IProps) => {
  const account = useUserAccount();
  const isChecked = (id: number): boolean => {
    if (isAll) {
      return !unCheckedIds.includes(id);
    }
    return checkedIds.includes(id);
  };

  const isAllChecked = (): boolean => {
    return isAll && unCheckedIds.length === 0;
  };

  // const isDisableDownloadCsv =
  //   !checkedIds.length && !(!checkedIds?.length && isAll);

  const canMarkAsPaid = (reservation: IndexReservation) => {
    if (reservation.order_status !== OrderStatus.UNPAID) return false;

    const now = new Date();
    const reservationStart = reservation.reservation_start_date
      ? reservation.reservation_start_date
      : reservation.service.hold_date?.[0]?.start;
    if (reservationStart && now >= new Date(reservationStart)) return false;

    return true;
  };

  const canEditReservation = (reservation: IndexReservation) => {
    if (reservation.service.type !== ServiceType.CALENDAR_RESERVATION) {
      return false;
    }

    const valids: number[] = [
      OrderSettlementMethod.FREE,
      OrderSettlementMethod.BANK,
      OrderSettlementMethod.LOCAL_PAYMENT,
    ];
    if (!valids.includes(reservation.payment_method as number)) {
      return false;
    }

    const now = new Date();
    const reservationStart = reservation.reservation_start_date
      ? reservation.reservation_start_date
      : reservation.service.hold_date?.[0]?.start;
    if (reservationStart && now < new Date(reservationStart)) return true;

    return false;
  };

  const styles = {
    table:
      "rounded-md border border-[#DFDFE2] w-full box-border text-[#1A1A1C] table-fixed md:table-auto",
    tableMobile:
      "rounded-md border border-[#DFDFE2] w-full box-border table-fixed",
    headerItem:
      "px-2 text-[#4C4C4C] text-left leading-[21px] font-bold px-4 border-r border-[#DFDFE2]",
    cell: "px-4 text-sm font-normal relative after:content-[''] after:h-[13px] after:w-[1px] after:absolute after:right-0 after:top-1/2 after:translate-y-[-50%] after:bg-[#DFDFE2] break-all",
  };

  const isDisableDownloadCsv = !account?.user_plan_config?.export_csv;
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const handleDownloadCsv = () => {
    if (isDisableDownloadCsv) {
      setIsPopupVisible(true);
      return;
    }

    handleClickCsv();
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  const actionChangeStatus = async (orderId: number, status: number) => {
    status === OrderStatus.UNPAID
      ? await reservationApi.updateReservationComplete({ id: orderId })
      : await reservationApi.updateReservationUnpaid({ id: orderId });

    handleRefreshReservations();
  };

  const getOption = useCallback((orderId: number, status: OrderStatus) => {
    const options = [
      {
        id: 0,
        child: (
          <div
            className="flex text-[#000000D9] text-xs font-normal leading-[22px] px-3 py-[5px] w-full"
            onClick={() => {
              void actionChangeStatus(orderId, status);
            }}
          >
            {updateStatusString(status)}
          </div>
        ),
      },
    ];
    return options;
  }, []);

  const hasChangeStatus = (method: number, status: number) => {
    const methods: number[] = [
      OrderSettlementMethod.BANK,
      OrderSettlementMethod.LOCAL_PAYMENT,
    ];

    const statuses: number[] = [OrderStatus.BILLED, OrderStatus.UNPAID];

    return methods.includes(method) && statuses.includes(status);
  };

  const getServiceMenuName = (reservation: IndexReservation) => {
    if (reservation.service?.hold_date?.length > 0) {
      return reservation.service?.hold_date?.map((item) => (
        <p
          key={`service_menu_name_${item.service_menu_name}`}
          className="break-all"
        >
          {item?.service_menu_name}{" "}
          {item?.quantity ? `：${item?.quantity}枚` : ""}
        </p>
      ));
    }
    return (
      <div className="break-all">
        {reservation.service_menu_name}
        {reservation.time_required && (
          <span className="inline-block">
            （{reservation.time_required}分）
          </span>
        )}
      </div>
    );
  };

  const getSubscriptionStatus = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.UNPAID:
        return SubscriptionContractStatus.PAYMENT_FAILED;
      case OrderStatus.WAITINGCANCEL:
        return SubscriptionContractStatus.CANCELLATION_ACCEPTED;
      case OrderStatus.CANCEL:
        return SubscriptionContractStatus.CANCELLED;
      case OrderStatus.TRIALING:
        return SubscriptionContractStatus.TRIALING;
      default:
        return SubscriptionContractStatus.ACTIVE;
    }
  };

  return (
    <>
      <div className="flex justify-end py-4 rounded-t-lg border border-theme-light md:justify-between md:border-0 mt-3 md:mt-0">
        <div className="flex gap-4 px-4 md:px-0">
          <div className="hidden text-right md:block">
            <OutlineButton
              color="primary"
              onClick={handleDownloadCsv}
              size="md"
              customClass="flex items-center !h-9"
              // disabled={!isAll && checkedIds.length === 0}
            >
              <span className="text-base font-bold leading-normal">
                CSV出力
              </span>
            </OutlineButton>
          </div>
          <Select
            onChange={(o) => {
              handleClickSort(o as SortDirection);
            }}
            options={sortOptions}
            value={sortState ? String(sortState) : "DESC"}
            className="w-[228px] md:w-[200px]"
            size="sm"
          />
        </div>
        {pagination && (
          <div className="hidden md:block">
            <Pagination
              current={pagination.current}
              max={pagination.last}
              pushPage={(page, per) => handlePagination(page, per)}
              perPage={pagination.per}
              total={pagination.total}
            />
          </div>
        )}
      </div>
      <div className="overflow-x-auto text-sm md:text-md">
        <table className={styles.table}>
          <thead>
            <tr className="bg-[#F7F8F9]">
              <th className="w-[50px] py-2 hidden">
                <div className={`${styles.headerItem} text-center border-r-0`}>
                  <Checkbox
                    id="checked-all"
                    checked={isAllChecked()}
                    onChange={handleClickAll}
                    className="border-color-gray-600"
                  />
                </div>
              </th>
              <th className="w-[200px] py-2">
                <div className={`${styles.headerItem}`}>ステータス</div>
              </th>
              <th className="w-[100px] md:w-[84px] py-2">
                <div className={`${styles.headerItem}`}>注文番号</div>
              </th>
              <th className="w-[130px] py-2">
                <div className={`${styles.headerItem}`}>氏名</div>
              </th>
              <th className="w-[240px] md:w-[220px] py-2">
                <div className={`${styles.headerItem}`}>コンテンツ名</div>
              </th>
              <th className="w-[130px] md:w-[180px] py-2">
                <div className={`${styles.headerItem}`}>申込メニュー</div>
              </th>
              <th className="w-[130px] md:w-[160px] py-2">
                <div className={`${styles.headerItem}`}>予約日時</div>
              </th>
              <th className="w-[130px] md:w-[120px] py-2">
                <div className={`${styles.headerItem}`}>支払情報</div>
              </th>
              <th className="w-[130px] md:w-[160px] py-2">
                <div className={`${styles.headerItem}`}>受付日時</div>
              </th>
              <th className="w-[140px] md:w-fit py-2"></th>
            </tr>
          </thead>
          <tbody>
            {reservations.map((reservation) => {
              const reservationEnd = reservation.reservation_end_date
                ? reservation.reservation_end_date
                : reservation.service.hold_date?.[0]?.end;
              const reservationStart = reservation.reservation_start_date
                ? reservation.reservation_start_date
                : reservation.service.hold_date?.[0]?.start;

              return (
                <tr key={reservation.id} className="border">
                  <td className="py-[11px] hidden">
                    <div className={`${styles.cell} text-center after:hidden`}>
                      <Checkbox
                        id={`checked-${reservation.id}`}
                        checked={isChecked(reservation.id)}
                        onChange={() => handleClickCheckbox(reservation.id)}
                        className="border-color-gray-600"
                      />
                    </div>
                  </td>
                  <td className="py-[11px]">
                    <div className={`${styles.cell} flex gap-2`}>
                      {reservation.service.type ===
                      ServiceType.SUBSCRIPTION_SERVICE ? (
                        <SubscriptionOrderStatusBadge
                          statusVal={getSubscriptionStatus(
                            reservation.order_status,
                          )}
                          divClassName="flex items-center w-[99px]"
                        />
                      ) : (
                        <ReservationOrderStatusBadge
                          statusVal={reservation.order_status}
                          divClassName="flex items-center w-[99px]"
                        />
                      )}
                      {reservation.service.type !==
                        ServiceType.SUBSCRIPTION_SERVICE &&
                        hasChangeStatus(
                          reservation.payment_method as number,
                          reservation.order_status,
                        ) && (
                          <Dropdown
                            menu={getOption(
                              reservation.id,
                              reservation.order_status,
                            )}
                            classNameBox="divide-y-0 px-0 w-[141px] z-10 py-1 left-0"
                          >
                            <OutlineButton
                              color="primary"
                              size="md"
                              spSize="sm"
                              customClass="flex items-center !h-9 !p-[10px]"
                            >
                              <span className="text-xs md:text-[14px] font-bold leading-normal tracking-normal">
                                変更
                              </span>
                            </OutlineButton>
                          </Dropdown>
                        )}
                    </div>
                  </td>
                  <td className="py-[11px]">
                    <div className={styles.cell}>{reservation.id}</div>
                  </td>
                  <td className="py-[11px]">
                    <div className={styles.cell}>
                      <p className="break-all">{reservation.customer_name}</p>
                    </div>
                  </td>
                  <td className="py-[11px]">
                    <div className={styles.cell}>
                      <p className="break-all">{reservation.service.name}</p>
                    </div>
                  </td>

                  <td className="py-[11px]">
                    <div className={styles.cell}>
                      {getServiceMenuName(reservation)}
                    </div>
                  </td>

                  <td className="py-[11px]">
                    <div className={styles.cell}>
                      {reservation.service.type !==
                        ServiceType.SUBSCRIPTION_SERVICE &&
                        reservationStart &&
                        reservationEnd && (
                          <>
                            {format(new Date(reservationStart), DATE_FORMAT_2)}
                            <br />
                            {`${format(
                              new Date(reservationStart),
                              HOURS_MINUTES_FORMAT,
                            )}-${format(
                              new Date(reservationEnd),
                              HOURS_MINUTES_FORMAT,
                            )}`}
                          </>
                        )}
                    </div>
                  </td>

                  <td className="py-[11px]">
                    <div className={styles.cell}>
                      <p className="break-all">
                        {orderSettlementMethodToString(
                          reservation.payment_method as number,
                        )}
                      </p>
                      {reservation.service.type !==
                        ServiceType.SUBSCRIPTION_SERVICE &&
                        (reservation.payment_method as number) !=
                          OrderSettlementMethod.FREE && (
                          <p className="break-all">
                            ¥
                            {formatNumber(reservation.purchase_price as number)}
                          </p>
                        )}
                    </div>
                  </td>

                  <td className="py-[11px]">
                    <div className={styles.cell}>
                      {format(new Date(reservation.reserved_at), DATE_FORMAT_2)}
                      <br />
                      {format(
                        new Date(reservation.reserved_at),
                        HOURS_MINUTES_FORMAT,
                      )}
                    </div>
                  </td>

                  <td className="py-[11px]">
                    <div className="shrink-0 flex items-center justify-end gap-x-3 gap-y-2 px-4 flex-wrap md:flex-nowrap">
                      {/* {canMarkAsPaid(reservation) && (
                        <Button
                          type="button"
                          color="primary"
                          label="決済済みにする"
                          size="sm"
                          onClick={() =>
                            handleClickPaymentConfirm(reservation.id)
                          }
                          className="md:min-w-[145px]"
                        />
                      )} */}
                      {canEditReservation(reservation) && (
                        <Button
                          type="button"
                          color="primary-outline"
                          label="申込変更"
                          size="sm"
                          onClick={() => handleEditReservation(reservation.id)}
                          className="md:min-w-[75px] md:px-[8.5px] py-[7.5px] tracking-normal"
                        />
                      )}
                      <Button
                        type="button"
                        label="詳細"
                        color="primary"
                        size="sm"
                        onClick={() => handleClickShow(reservation.id)}
                        className="md:w-[64px] w-[90px]"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isPopupVisible && <ErrorLimitByPlanModal onClose={handleClosePopup} reason="ORDER_CSV_EXPORT" />}
    </>
  );
};

export default memo(ReservationTable);
